import React from 'react';
import { action, observable } from 'mobx';
import { observer, Provider } from 'mobx-react';
import { Router } from 'react-router-dom';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

import Routes from './routes';

import { applyDecorators } from "@app/Helpers/Decorator";
import { Configurations } from './AppConstants/Configurations';

import { appStore } from './AppStore';
import { configurationStore } from './Stores/ConfigurationStore';
import { history, navigationStore } from './Stores/NavigationStore';
import { permissionStore } from './Stores/PermissionStore';
import { personActivityStore } from './Stores/PersonActivityStore';

import { AppReadyService, AppReadyDisposeHandler } from './Services/AppReadyService';
import { service } from './Services/_ServicesRegister';
import { requestTimelineService } from './Services/RequestTimelineService';
import { emitter } from './Services/EventEmitter';

import ErrorBoundary from './Components/ErrorBoundary';
import { NotificationContainer } from './Components/ToastNotification';
import { ToolTipContainer } from '@app/Components';
import { DateTimePickerContainer } from '@app/Components/DateTimeControls';
import { ChatContainer } from '@app/Components/ExplainErrorChat/ExplainErrorChat';

interface IApplicationInsightsConfigWindow {
    aiConfig?: {
        instrumentationKey?: string;
    };
}

type AppProps = {
    callback: () => void;
};

@observer
export default class App extends React.Component<AppProps> {
    private _appReadyService: AppReadyService = service.resolve(AppReadyService);
    private _appReadyDisposeCallback: AppReadyDisposeHandler | null = null;
    @observable applicationInsights: ApplicationInsights | null = null;
    
    constructor(props: AppProps) {
        super(props);
        applyDecorators(this);
    }
    
    async componentDidMount() {
        this.props.callback();
        navigationStore.init();
        requestTimelineService.init();

        this.initApplicationInsights();
        
        emitter.on('trackPageView', this._trackPageView);

        void configurationStore.get(Configurations.featureGroup, Configurations.enablePWA).then(async (res) => {
            if (res === 'false' && navigator.serviceWorker) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                registrations.forEach((reg) => reg.unregister());
                void caches.keys().then((cacheNames) => Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName))));
            }
        });

        configurationStore.loadConfig();
        this._appReadyDisposeCallback = this._appReadyService.onAppReady(() => {
            appStore.setAppReady();
            personActivityStore.startChecking();
        });
    }

    componentWillUnmount() {
        this._appReadyDisposeCallback?.();
        personActivityStore.stopChecking();
        emitter.off('trackPageView', this._trackPageView);
    }

    render() {
        return (
            <ErrorBoundary>
                <NotificationContainer />
                {appStore.isAppReady ? (
                    <Provider>
                        {/*<React.StrictMode>*/}
                        <Router history={history}>{Routes.routs}</Router>
                        {/*</React.StrictMode>*/}
                    </Provider>
                ) : (
                    <div />
                )}
                <ToolTipContainer />
                <DateTimePickerContainer />
                <ChatContainer />
            </ErrorBoundary>
        );
    }

    @action.bound
    private _trackPageView(){
        this.applicationInsights?.trackPageView();
    }


    public initApplicationInsights () {
        const instrumentationKey = (window as IApplicationInsightsConfigWindow).aiConfig?.instrumentationKey;
        if (instrumentationKey) {
            //https://github.com/microsoft/applicationinsights-react-js
            const reactPlugin = new ReactPlugin();
            this.applicationInsights = new ApplicationInsights({
                config: {
                    instrumentationKey: instrumentationKey,
                    extensions: [reactPlugin],
                    enableAutoRouteTracking: true,
                    extensionConfig: {
                        [reactPlugin.identifier]: { history: history }
                    }
                }
            });
            this.applicationInsights.loadAppInsights();
            this.applicationInsights.trackPageView();
        }
    }
}